import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { sendWelcomeEmail } from "@/lib/email";

/**
 * Callback de autenticación para flujos con código (OAuth / verificación de email).
 * Intercambia el código PKCE por una sesión. Tras establecer la sesión, el
 * AuthProvider (vía onAuthStateChange) carga el perfil automáticamente.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");

    // Defensa: si llega un flujo de recuperación, reenviar a reset-password.
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    if (hashParams.get("type") === "recovery") {
      navigate("/reset-password", { replace: true });
      return;
    }

    if (!code) {
      setError("No se recibió un código de autenticación.");
      return;
    }

    supabase.auth
      .exchangeCodeForSession(code)
      .then(({ error: exchangeError }) => {
        if (exchangeError) {
          setError(exchangeError.message);
          return;
        }
        // Disparo de bienvenida (idempotente) tras login por OAuth / email.
        sendWelcomeEmail().catch(() => {
          // Silencioso.
        });
        navigate("/", { replace: true });
      })
      .catch(() => {
        setError("No se pudo completar la autenticación.");
      });
  }, [navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background-50 px-4 text-center">
      {error ? (
        <>
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100">
            <i className="ri-close-circle-fill text-3xl text-accent-600"></i>
          </span>
          <h1 className="font-heading text-xl font-bold text-foreground-950">
            No se pudo iniciar sesión
          </h1>
          <p className="max-w-sm text-sm text-foreground-600">{error}</p>
        </>
      ) : (
        <>
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          </span>
          <p className="text-sm text-foreground-600">Verificando tu sesión…</p>
        </>
      )}
    </div>
  );
}